import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import DataTable from '../../components/DataTable';
import api from '../../services/api';
import { toast } from 'sonner';
import { useBusinessTimezone, formatDateOnlyDisplay } from '../../utils/businessTime';
import {
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  Filter,
  TrendingUp,
  AlertTriangle,
} from 'lucide-react';

const AdminAttendance = () => {
  const businessTz = useBusinessTimezone();
  const [attendance, setAttendance] = useState([]);
  const [classes, setClasses] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedProgram, setSelectedProgram] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchAttendance();
  }, [selectedClass, selectedProgram, dateFilter, statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [classesRes, programsRes] = await Promise.all([
        api.get('/classes'),
        api.get('/programs')
      ]);
      setClasses(classesRes.data || []);
      setPrograms(programsRes.data || []);
      await fetchAttendance();
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendance = async () => {
    try {
      let url = '/attendance';
      const params = new URLSearchParams();
      
      if (selectedClass && selectedClass !== 'all') {
        params.append('class_id', selectedClass);
      }
      if (dateFilter) {
        params.append('date', dateFilter);
      }
      
      if (params.toString()) {
        url += '?' + params.toString();
      }
      
      const response = await api.get(url);
      let data = response.data || [];
      
      // Client-side filtering for program and status
      if (selectedProgram && selectedProgram !== 'all') {
        data = data.filter(a => a.program_id === selectedProgram);
      }
      if (statusFilter && statusFilter !== 'all') {
        data = data.filter(a => a.status === statusFilter);
      }
      
      setAttendance(data);
    } catch (error) {
      console.error('Error fetching attendance:', error);
      setAttendance([]);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      present: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Present' },
      absent: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Absent' },
      late: { color: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Late' },
      excused: { color: 'bg-blue-100 text-blue-700', icon: Calendar, label: 'Excused' }
    };
    const config = statusConfig[status] || statusConfig.present;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} capitalize flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  // Calculate stats
  const calculateStats = () => {
    const total = attendance.length;
    const present = attendance.filter(a => a.status === 'present').length;
    const absent = attendance.filter(a => a.status === 'absent').length;
    const late = attendance.filter(a => a.status === 'late').length;
    const excused = attendance.filter(a => a.status === 'excused').length;
    const rate = total > 0 ? Math.round((present / total) * 100) : 0;

    return { total, present, absent, late, excused, rate };
  };

  const stats = calculateStats();

  // Get low attendance players (below 70%)
  const getLowAttendancePlayers = () => {
    const playerStats = {};
    
    attendance.forEach(record => {
      if (!playerStats[record.player_id]) {
        playerStats[record.player_id] = {
          player_id: record.player_id,
          player_name: record.player_name,
          total: 0,
          present: 0
        };
      }
      playerStats[record.player_id].total++;
      if (record.status === 'present' || record.status === 'late') {
        playerStats[record.player_id].present++;
      }
    });

    return Object.values(playerStats)
      .map(p => ({
        ...p,
        rate: p.total > 0 ? Math.round((p.present / p.total) * 100) : 0
      }))
      .filter(p => p.rate < 70 && p.total >= 3)
      .sort((a, b) => a.rate - b.rate)
      .slice(0, 5);
  };

  const lowAttendancePlayers = getLowAttendancePlayers();

  const columns = [
    {
      key: 'date',
      label: 'Date',
      sortable: true,
      render: (row) => (
        <span className="font-medium">
          {/^\d{4}-\d{2}-\d{2}$/.test(String(row.date || ''))
            ? formatDateOnlyDisplay(row.date, { weekday: 'short', month: 'short', day: 'numeric', year: undefined })
            : new Date(row.date).toLocaleDateString('en-US', {
                timeZone: businessTz,
                weekday: 'short',
                month: 'short',
                day: 'numeric'
              })}
        </span>
      )
    },
    {
      key: 'player_name',
      label: 'Player',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => (
        <Link 
          to={`/admin/players/${row.player_id}`}
          className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
        >
          {row.player_name}
        </Link>
      )
    },
    {
      key: 'class_name',
      label: 'Class',
      sortable: true,
      filterable: true,
      filterType: 'text'
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => getStatusBadge(row.status)
    },
    {
      key: 'notes',
      label: 'Notes',
      render: (row) => (
        <span className="text-slate-500 max-w-xs truncate block">
          {row.notes || '-'}
        </span>
      )
    },
    {
      key: 'marked_by_name',
      label: 'Marked By',
      render: (row) => (
        <span className="text-sm text-slate-500">{row.marked_by_name || '-'}</span>
      )
    }
  ];

  const clearFilters = () => {
    setSelectedClass('all');
    setSelectedProgram('all');
    setDateFilter('');
    setStatusFilter('all');
  };

  const hasActiveFilters = selectedClass !== 'all' || selectedProgram !== 'all' || dateFilter || statusFilter !== 'all';

  if (loading) {
    return (
      <DashboardLayout title="Attendance">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Attendance">
      <div className="space-y-6" data-testid="admin-attendance-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Attendance Management</h1>
            <p className="text-slate-500">Track and manage player attendance across all classes</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Records</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <Calendar className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Present</p>
                  <p className="text-2xl font-bold text-green-600">{stats.present}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Absent</p>
                  <p className="text-2xl font-bold text-red-600">{stats.absent}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <XCircle className="h-5 w-5 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Late</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.late}</p>
                </div>
                <div className="p-3 bg-amber-100 rounded-full">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Attendance Rate</p>
                  <p className={`text-2xl font-bold ${stats.rate >= 80 ? 'text-green-600' : stats.rate >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                    {stats.rate}%
                  </p>
                </div>
                <div className="p-3 bg-slate-100 rounded-full">
                  <TrendingUp className="h-5 w-5 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Low Attendance Alert */}
        {lowAttendancePlayers.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="h-5 w-5" />
                Low Attendance Alert
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-amber-600 mb-3">
                The following players have attendance below 70%:
              </p>
              <div className="flex flex-wrap gap-2">
                {lowAttendancePlayers.map((player) => (
                  <Link
                    key={player.player_id}
                    to={`/admin/players/${player.player_id}`}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-white rounded-full border border-amber-200 hover:border-amber-300 transition-colors"
                  >
                    <span className="font-medium text-slate-700">{player.player_name}</span>
                    <Badge className="bg-red-100 text-red-700 text-xs">
                      {player.rate}%
                    </Badge>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-400" />
                <span className="text-sm font-medium text-slate-700">Filters:</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Label className="text-sm">Program:</Label>
                <Select value={selectedProgram} onValueChange={setSelectedProgram}>
                  <SelectTrigger className="w-48" data-testid="program-filter">
                    <SelectValue placeholder="All Programs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Programs</SelectItem>
                    {programs.map((program) => (
                      <SelectItem key={program.id} value={program.id}>
                        {program.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <Label className="text-sm">Class:</Label>
                <Select value={selectedClass} onValueChange={setSelectedClass}>
                  <SelectTrigger className="w-48" data-testid="class-filter">
                    <SelectValue placeholder="All Classes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Classes</SelectItem>
                    {classes.map((cls) => (
                      <SelectItem key={cls.id} value={cls.id}>
                        {cls.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <Label className="text-sm">Date:</Label>
                <Input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="w-40"
                  data-testid="date-filter"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Label className="text-sm">Status:</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-36" data-testid="status-filter">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="present">Present</SelectItem>
                    <SelectItem value="absent">Absent</SelectItem>
                    <SelectItem value="late">Late</SelectItem>
                    <SelectItem value="excused">Excused</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {hasActiveFilters && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={clearFilters}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Attendance Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Attendance Records
              {attendance.length > 0 && (
                <Badge variant="outline" className="ml-2">
                  {attendance.length} records
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {attendance.length > 0 ? (
              <DataTable
                columns={columns}
                data={attendance}
                searchable={true}
                searchPlaceholder="Search by player or class..."
                itemsPerPage={20}
              />
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Calendar className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p className="text-lg font-medium">No attendance records found</p>
                <p className="text-sm">
                  {hasActiveFilters 
                    ? 'Try adjusting your filters to see more results.'
                    : 'Attendance records will appear here once coaches mark attendance.'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminAttendance;
